import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import SpiningLoader from '@/components/common/SpinningLoader';
import type {BusinessPoolAccount} from '@/types';
import * as formatDate from '@/utils/date';

import PoolAccountUpdateButton from './PoolAccountUpdateButton';

type PoolAccountSummaryProp = {
  item: BusinessPoolAccount;
  isLoading?: boolean;
  isDetail?: boolean;
};

/**
 * `extraProps` is a free-form `Record<string, unknown>`, so this only trusts
 * the types whose default stringification is meaningful. A genuine
 * object/array renders as JSON rather than the uninformative
 * `[object Object]`.
 */
const toDisplayString = (value: unknown): string => {
  if (value == null) {
    return '';
  }
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  return JSON.stringify(value);
};

const PoolAccountSummary = ({item, isLoading}: PoolAccountSummaryProp) => {
  return isLoading ? (
    <SpiningLoader />
  ) : (
    <div
      className="whitespace-normal break-words bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5"
      data-cy="merchant-pool-accounts-summary"
    >
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-xl mr-3">{item.accountName}</h2>
      </div>

      <div>
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Pool Account ID
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.id}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Account Name
          </p>
          <p
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
            data-cy="merchant-pool-accounts-summary-account-name"
          >
            {item?.accountName}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Account Number
          </p>
          <p
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
            data-cy="merchant-pool-accounts-summary-account-number"
          >
            {item?.accountNumber}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Institution
          </p>
          <p
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
            data-cy="merchant-pool-accounts-summary-institution"
          >
            {item?.institutionName}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Country</p>
          <p
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
            data-cy="merchant-pool-accounts-summary-country"
          >
            {item?.countryCode}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Channel</p>
          <p
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
            data-cy="merchant-pool-accounts-summary-channel"
          >
            {item?.channel?.replaceAll('_', ' ')}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Status</p>
          <p
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100"
            data-cy="merchant-pool-accounts-summary-status"
          >
            {!item?.meta?.flags?.isDeactivated ? (
              <span className="text-green-500">Active</span>
            ) : (
              <span className="text-red-500">Inactive</span>
            )}
          </p>
        </div>

        {item?.meta?.extraProps &&
          Object.entries(item.meta.extraProps).map(([key, value]) => (
            <div
              key={key}
              className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between"
            >
              <p className="text-slate-500 text-sm dark:text-slate-100">
                {key}
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                {toDisplayString(value)}
              </p>
            </div>
          ))}
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Date Created
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {formatDate.DDMonYYYY(item?.createdAt?.toString() || '')}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-start gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100 shrink-0 whitespace-nowrap">
            Last Activity
          </p>
          <p
            className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 min-w-0 break-words whitespace-normal text-wrap"
            data-cy="merchant-pool-accounts-summary-last-activity"
          >
            {item?.meta?.logs?.[item?.meta?.logs.length - 1]}
          </p>
        </div>
      </div>

      <div className="text-sm text-slate-500 w-full pt-5 sm:pt-5">
        <div className="flex justify-end gap-4">
          <PermissionsProvider
            permission={AfriexAdminPermission.BusinessPoolAccountManage}
          >
            <PoolAccountUpdateButton initialData={item} />
          </PermissionsProvider>
        </div>
      </div>
    </div>
  );
};

export default PoolAccountSummary;
